<template>
	<!-- Skip Links for Keyboard Navigation — teleported before v-main for first-focus order -->
	<Teleport to="#skip-links-portal">
		<a href="#main-content" class="skip-link" @click.prevent="() => { const el = document.getElementById('main-content'); if (el) el.focus(); }">Skip to main content</a>
	</Teleport>
	
    <div class="bg-color-granite pb-10" role="main" aria-label="My Performance Dashboard">
		<div class="d-flex justify-center align-center w-100 position-relative" role="banner" aria-label="My Performance Banner">
			<v-img class="w-100 h-100"			
				:height="199"
				aspect-ratio="16/9"
				src="https://dellpartnerincentives.com/siteassets/Images/HomePartner/Banner/bgRISE2.jpg"
				alt="RISE My Performance Program Banner"
			></v-img>
			<div class="position-absolute page-header">
				<h1>    <LanguagePlaceholder default-text="My Performance" /></h1>
			</div>			
		</div>
        <section class="bg-color-granite w-100 h-100" role="region" aria-label="Introduction section" id="main-content">
            <div class="bg-color-quartz w-100 h-100 my-2">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0 pt-2 pt-md-3 bg-color-dell-blue tx-color-white">
                    <v-col cols="12" md="10" class="d-flex flex-column justify-start w-100 pa-0 py-4 py-md-8 px-2 px-md-0 tx-font-normal tx-font-size20">  
                        <v-row no-gutters>
                            <div class="body-text">
                            <p class="pb-2">
                                <LanguagePlaceholder is-html
                                    default-text="​​​​​​​​​​​Track your RISE performance throughout the incentive period to see how much you have earned and what you need to do to reach your reward multiplier thresholds." />
                            </p>
            
                            <p class="pb-2">
                                <LanguagePlaceholder is-html
                                    default-text="​​​​​You can see which products are eligible for rewards on the <route class='dell-white' route-name='home-eligible-products' style='text-decoration: underline;' tabindex='0' role='link' aria-label='Go to Eligible Products'>Eligible Products</route> page. On this page, you can also check which products count towards your reward multiplier thresholds." />
                            </p>
                   
                            <p class="pb-2">
                                <LanguagePlaceholder is-html
                                    default-text="For more information and support on anything related to your purchases, rewards or your performance, visit the <route class='dell-white' route-name='faq' style='text-decoration: underline;' tabindex='0' role='link' aria-label='Go to Help Center'>Help Center</route>." />
                            </p>
                
                        </div>
                        </v-row>          
                    </v-col>
                </v-row>
            </div>
        </section>
        <section class="bg-color-granite w-auto h-100 my-2 mx-10" role="region" aria-label="Incentive period selection" id="performance-overview">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100">
            <v-col cols="12" md="1" class="bg-color-quartz pa-2 pa-md-5"></v-col>
            <v-col cols="12" md="10" class="d-flex justify-start flex-column w-100 my-2 my-md-5 pb-5 pb-md-10 tx-font-bold bg-color-white px-2 px-md-0">  
                <div class="ma-0 pa-3 pa-md-5 mb-5 mb-md-10 bg-color-cobalt tx-color-white tx-font-normal">
                    <LanguagePlaceholder is-html
                                            default-text="​​​​​​Use the 'Incentive Period' dropdown to view your performance across different RISE incentive periods. Your report will reflect the reward multipliers and thresholds that were applicable in that incentive period. Data is provided by Distributors 2 weeks in arrears." />
                </div>
                <v-select ref="quarterSelectRef" v-model="selectedQuarter" class="w-100 w-md-25 ps-0 ps-md-14 mb-2"
                    :items="quarters" variant="outlined"
                    label="Select Incentive Period"
                    @keydown.enter.stop.prevent
                    @update:menu="(val) => quarterMenuOpen = val"
                    :aria-describedby="'quarter-help'"
                    id="quarter-select">
                    <template v-slot:label>
                        <LanguagePlaceholder
                            is-html
                            default-text="Select Incentive Period" />
                    </template>
                </v-select>
                <div id="quarter-help" class="sr-only">
                    <LanguagePlaceholder default-text="Choose the incentive period to view your performance data" />
                </div>
                <div class="w-25 ps-14">
                    <v-btn @click="clickSubmit" @keydown.enter="clickSubmit" @keydown.space.prevent="clickSubmit" color="primary" variant="outlined" aria-label="Submit to load performance data" tabindex="0">
                        <LanguagePlaceholder default-text="Submit" />
                    </v-btn>
                    </div>
                </v-col>
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
            </v-row>
        </section>

        <section class="bg-color-granite w-auto h-100 my-2 mx-10" role="region" aria-label="Performance metrics overview">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="1" class="bg-color-quartz"></v-col>
                <v-col cols="10" class="bg-color-white my-5 d-flex flex-column justify-center w-100 tx-font-bold">  
                    <v-row no-gutters class="w-100" role="group" aria-label="Total rewards summary">
                        <v-col cols="12" md="4">
                            <v-card class="w-100 pa-5 elevation-0" role="region" aria-label="Total Core Rewards">
                                <v-card-title class="text-subtitle-1"
                                    style="background-color: #0C32A4; color:#FFFFFF;">
                                    <LanguagePlaceholder default-text="Total $ Core Rewards" />
                                </v-card-title>
                                <v-card-text class="text-center pt-4" style="background-color: #0672CB;"
                                    color="#FFFFFF">
                                    <div class="tx-color-white tx-font-bolder tx-font-size32">
                                        {{ $filters.currencyUSD(myPerformanceData.totalDollarCoreRewards) }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-card class="w-100 pa-5 elevation-0" role="region" aria-label="Total Bonus Rewards">
                                <v-card-title class="text-subtitle-1"
                                    style="background-color: #0C32A4; color:#FFFFFF;">
                                    <LanguagePlaceholder default-text="Total $ Bonus Rewards" />
                                </v-card-title>
                                <v-card-text class="text-center pt-4" style="background-color: #0672CB;"
                                    color="#FFFFFF">
                                    <div class="tx-color-white tx-font-bolder tx-font-size32">
                                        {{ $filters.currencyUSD(myPerformanceData.totalDollarBonusRewards) }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-card class="w-100 pa-5 elevation-0" role="region" aria-label="Total Rewards">
                                <v-card-title class="text-subtitle-1"
                                    style="background-color: #0C32A4; color:#FFFFFF;">
                                    <LanguagePlaceholder default-text="Total $ Rewards" />
                                </v-card-title>
                                <v-card-text class="text-center pt-4" style="background-color: #0672CB;"
                                    color="#FFFFFF">
                                    <div class="tx-color-white tx-font-bolder tx-font-size32">
                                        {{ $filters.currencyUSD(myPerformanceData.totalDollarRewards) }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <div class="d-flex flex-column flex-md-row justify-center w-100 gap-2 gap-md-0 mt-2 mt-md-0">
                        <v-card class="w-100 pa-3 pa-md-5 elevation-0 flex-grow-1">
                            <v-card-title class="text-subtitle-1"
                                style="background-color: #0C32A4; color:#FFFFFF;"><v-icon
                                    class="mx-2">mdi-keyboard</v-icon>
                                    <LanguagePlaceholder default-text="PCs+ Displays+Peripherals" />
                                    
                                </v-card-title>
                            <v-card-text class="text-center pa-0">
                                <v-row no-gutters class="text-body-1" style="height: 120px;">
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="mt-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units This Incentive Period (Nett inclusive of returns)" />
                                            
                                        </div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="my-2 my-md-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units Last Four Incentive Periods" />
                                            </div>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters class="text-body-1" style="color:#102053">
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="mt-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.csG_Units_ThisPeriod }}</div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="my-2 my-md-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.csG_Units_LastFourPeriods }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <v-card class="w-100 pa-3 pa-md-5 elevation-0 flex-grow-1">
                            <v-card-title class="text-subtitle-1"
                                style="background-color: #0C32A4; color:#FFFFFF;"><v-icon
                                    class="mx-2" aria-hidden="true">mdi-server</v-icon>
                                    <LanguagePlaceholder default-text="Server+" />
                                    </v-card-title>
                            <v-card-text class="text-center pa-0">
                                <v-row no-gutters class="text-body-1" style="min-height: 7.5rem;">
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="mt-2 mt-md-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units This Incentive Period" />
                                            
                                        </div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">

                                        <div class="my-2 my-md-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units Last Four Incentive Periods" />
                                            </div>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters class="text-body-1" style="color:#102053">
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="mt-2 mt-md-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.server_Units_ThisPeriod }}</div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="my-2 my-md-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.server_Units_LastFourPeriods }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <v-card class="w-100 pa-3 pa-md-5 elevation-0 flex-grow-1" >
                            <v-card-title class="text-subtitle-1"
                                style="background-color: #0C32A4; color:#FFFFFF;"><v-icon
                                    class="mx-2" aria-hidden="true">mdi-nas</v-icon>
                                    <LanguagePlaceholder default-text="Storage+" />
                                    </v-card-title>
                            <v-card-text class="text-center pa-0">
                                <v-row no-gutters class="text-body-1" style="min-height: 7.5rem;">
                                    <v-col class="pa-2" style="background-color: #0672CB">

                                        <div class="mt-2 mt-md-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units This Incentive Period" />
                                            </div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="my-2 my-md-4 tx-color-white tx-font-normal tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units Last Four Incentive Periods" />
                                            </div>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters class="text-body-1" style="color:#102053">
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="mt-2 mt-md-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.storage_Units_ThisPeriod }}</div>
                                    </v-col>
                                    <v-col class="pa-2" style="background-color: #0672CB">
                                        <div class="my-2 my-md-4 tx-color-white tx-font-bolder tx-font-size32">{{ myPerformanceData.storage_Units_LastFourPeriods }}
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </div>
                            
                </v-col>
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
            </v-row>
        </section>
        <section class="bg-color-granite w-auto h-100 my-2 mx-2 mx-md-10" role="region" aria-label="Reward multiplier thresholds">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
                <v-col cols="12" md="10" class="bg-color-cobalt tx-color-white mt-2 mt-md-5 py-3 py-md-5 px-3 px-md-10 d-flex justify-center w-100 tx-font-normal">  
                        <LanguagePlaceholder is-html
                            default-text="​​​​​​Use the progress bars below to see how many units you need to purchase to hit your reward multiplier thresholds. Your rewards will be multiplied by the amount stated, right back to unit one." />
                </v-col>
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
            </v-row>
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
                <v-col cols="12" md="10" class="bg-color-white mb-2 mb-md-5 pb-3 pb-md-5 d-flex justify-center w-100 tx-font-bold px-2 px-md-0">  
                    <div class="d-flex flex-wrap flex-row ga-2 ga-md-10 pt-3 pt-md-5 w-100" aria-hidden="true">
                            <PartnerRewardsProgress 
                                title="PCs+ Displays+Peripherals" 
                                subTitleLine1="Threshold 1    1.2x Rewards"
                                subTitleLine2="Threshold 2    1.3x Rewards"
                                subTitleLine3="Threshold 3    1.4x Rewards"
                                subTitleLine4="Threshold 4    1.5x Rewards"
                                subTitleLine5="Threshold 5    1.6x Rewards"
                                :labels="['Threshold 5  400 units', 'Threshold 4  250 units', 'Threshold 3  160 units', 'Threshold 2    80 units', 'Threshold 1    30 units']" 
                                :units=myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod
                                :totalUnits=myPerformanceData.csG_Units_ThisPeriod
                                :limit1="30" 
                                :limit2="80" 
                                :limit3="160" 
                                :limit4="250" 
                                :limit5="400" 
                                :reward-loading="false"
                            ></PartnerRewardsProgress>
                            <PartnerRewardsProgress 
                                title="SERVER+" 
                                subTitleLine1="Threshold 1    1.2x Rewards"
                                subTitleLine2="Threshold 2    1.3x Rewards"
                                subTitleLine3="Threshold 3    1.4x Rewards"
                                subTitleLine4="Threshold 4    1.5x Rewards"
                                subTitleLine5="Threshold 5    1.6x Rewards"
                                :labels="['Threshold 5  21 units', 'Threshold 4  17 units', 'Threshold 3  13 units', 'Threshold 2    9 units', 'Threshold 1    5 units']" 
                                :units=myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod
                                :totalUnits=myPerformanceData.server_Units_ThisPeriod
                                :limit1="5" 
                                :limit2="9" 
                                :limit3="13" 
                                :limit4="17" 
                                :limit5="21" 
                                :reward-loading="false"
                            ></PartnerRewardsProgress>
                            <PartnerRewardsProgress 
                                title="STORAGE+" 
                                subTitleLine1="Threshold 1    1.2x Rewards"
                                subTitleLine2="Threshold 2    1.3x Rewards"
                                subTitleLine3="Threshold 3    1.4x Rewards"
                                subTitleLine4="Threshold 4    1.5x Rewards"
                                subTitleLine5="Threshold 5    1.6x Rewards"
                                :labels="['Threshold 5  13 units', 'Threshold 4  11 units', 'Threshold 3   8 units', 'Threshold 2   5 units', 'Threshold 1   2 units']" 
                                :units="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod"
                                :totalUnits="myPerformanceData.storage_Units_ThisPeriod"
                                :limit1="2"
                                :limit2="5"
                                :limit3="8"
                                :limit4="11"
                                :limit5="13"
                                :reward-loading="false"
                            ></PartnerRewardsProgress>
                    </div>   
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
            </v-row>
        </section>
        <section class="bg-color-granite w-auto h-100 my-2 mx-2 mx-md-10" role="region" aria-label="Performance data sections">
            <v-expansion-panels multiple variant="accordion">

                <v-expansion-panel value="pcdp" id="pcs-displays-peripherals">
                    <v-expansion-panel-title aria-label="PCs+ Displays and Peripherals performance details, select to expand or collapse">
                        <v-icon class="me-2" aria-hidden="true">mdi-keyboard</v-icon>
                        <LanguagePlaceholder default-text="PCs+ Displays+Peripherals" />
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="pa-0">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
                <v-col cols="12" md="10" class="bg-color-white my-2 my-md-5 d-flex justify-center flex-column w-100 tx-font-bold px-2 px-md-0">  
                    <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                        <v-col cols="12" class="d-flex justify-start w-100 pa-0 py-5 py-md-10 ps-3 ps-md-10 tx-font-bold bg-color-quartz">  
                            <h2>
                                <LanguagePlaceholder default-text='PCs+ Displays+Peripherals'></LanguagePlaceholder>
                            </h2>   
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="8">
                            <v-card color="#1D65C0" class="elevation-0">
                                <v-card-text>
                                    <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units: " />
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.csG_Units_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                    <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds: " />                                        
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="12">
                            <v-timeline direction="horizontal">
                                <v-timeline-item
                                    :dot-color="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 30 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 30 && myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod < 80">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 1" />
                                            
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.2x Rewards)" /> 
                                        </div>
                                        <div> <LanguagePlaceholder default-text="(30 Units)" /> </div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 80 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 80 && myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod < 160">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 2" /> 
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.3x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(80 Units)" /> </div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 160 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 160 && myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod < 250">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 3" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.4x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(160 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 250 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 250 && myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod < 400">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 4" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.5x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(250 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 400 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod >= 400">
                                            {{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 5" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.6x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(400 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                            </v-timeline>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Total Eligible Units:" /></div>
                                    <div><strong>{{ myPerformanceData.csG_Units_ThisPeriod }}</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds:" />
                                    </div>
                                    <div><strong>{{ myPerformanceData.csG_UnitsCountTowardThreshold_ThisPeriod
                                            }}</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 1 (30 Units | 1.2x Rewards)" />
                                    </div>
                                    <div><strong>30</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 2 (80 Units | 1.3x Rewards)" />
                                    </div>
                                    <div><strong>80</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 3 (160 Units | 1.4x Rewards)" />
                                    </div>
                                    <div><strong>160</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 4 (250 Units | 1.5x Rewards)" />
                                    </div>
                                    <div><strong>250</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 5 (400 Units | 1.6x Rewards)" />
                                    </div>
                                    <div><strong>400</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-toolbar density="compact" color="#EEEEEE"></v-toolbar>
                    <v-row justify="center">
                        <v-col cols="12">
                            <v-card tile flat>
                                <v-tabs v-model="pcdpActiveTab" color="primary" aria-label="PCs+ Displays+Peripherals data view tabs">
                                    <v-tab value="eligible" tabindex="0" aria-label="View Eligible Sales for PCs+ Displays+Peripherals">
                                        <LanguagePlaceholder default-text="Eligible Sales" />
                                    </v-tab>
                                    <v-tab value="non-eligible" tabindex="0" aria-label="View Non Eligible Sales for PCs+ Displays+Peripherals">
                                        <LanguagePlaceholder default-text="Non Eligible Sales" />
                                    </v-tab>
                                </v-tabs>
                                <v-toolbar density="compact" color="#EEEEEE">
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-download" @click="downloadFile_csg" @keydown.enter="downloadFile_csg" @keydown.space.prevent="downloadFile_csg" aria-label="Download PCs+ Displays+Peripherals performance data" tabindex="0"><v-icon size="large">mdi-download</v-icon></v-btn>
                                </v-toolbar>
                                <v-tabs-window v-model="pcdpActiveTab">
                                    <v-tabs-window-item value="eligible">
                                        <v-card-text class="pa-0">
                                            <v-text-field v-model="searchStringPCDP" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                variant="outlined" hide-details single-line
                                                aria-label="Filter PCs+ Displays+Peripherals eligible sales data"
                                                :aria-describedby="'pcdp-filter-help'"
                                                id="pcdp-filter">
                                                <template v-slot:label>
                                                    <span>Filter PCs+ Displays+Peripherals</span>
                                                </template>
                                            </v-text-field>
                                            <div id="pcdp-filter-help" class="sr-only">
                                                <LanguagePlaceholder default-text="Type to filter the performance data table below" />
                                            </div>
                                            <v-data-table :items-per-page="10" :headers="headers" :search="searchStringPCDP"
                                                :items="myPerformanceData.csG_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                id="pcdp-performance-table"
                                                role="table" aria-label="PCs, Displays and Peripherals eligible sales performance data">
                                                <template v-slot:item.rewards="{ item }">
                                                    {{ $filters.currencyUSD(item.rewards) }}
                                                </template>
                                                <template v-slot:footer.prepend>
                                                    <span id="pcdp-items-per-page-label" class="sr-only">Items per page selector for PCs+ Displays+Peripherals table</span>
                                                </template>
                                                <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                    <span id="pcdp-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in PCs+ Displays+Peripherals table`">
                                                        {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                    </span>
                                                </template>
                                            </v-data-table>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                    <v-tabs-window-item value="non-eligible">
                                        <v-card-text class="pa-0">
                                            <v-text-field v-model="searchStringPCDPNonEligible" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                variant="outlined" hide-details single-line
                                                aria-label="Filter PCs+ Displays+Peripherals non eligible sales data"
                                                :aria-describedby="'pcdp-non-eligible-filter-help'"
                                                id="pcdp-non-eligible-filter">
                                                <template v-slot:label>
                                                    <span>Filter PCs+ Displays+Peripherals</span>
                                                </template>
                                            </v-text-field>
                                            <div id="pcdp-non-eligible-filter-help" class="sr-only">
                                                <LanguagePlaceholder default-text="Type to filter the non eligible performance data table below" />
                                            </div>
                                            <v-data-table :items-per-page="10" :headers="nonEligibleHeaders" :search="searchStringPCDPNonEligible"
                                                :items="myNonEligiblePerformanceData.csG_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                id="pcdp-non-eligible-performance-table"
                                                role="table" aria-label="PCs, Displays and Peripherals non eligible sales performance data">
                                                <template v-slot:item.rewards="{ item }">
                                                    {{ $filters.currencyUSD(item.rewards) }}
                                                </template>
                                                <template v-slot:footer.prepend>
                                                    <span id="pcdp-non-eligible-items-per-page-label" class="sr-only">Items per page selector for PCs+ Displays+Peripherals non eligible table</span>
                                                </template>
                                                <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                    <span id="pcdp-non-eligible-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in PCs+ Displays+Peripherals non eligible table`">
                                                        {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                    </span>
                                                </template>
                                            </v-data-table>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                </v-tabs-window>
                                <v-card>
                                    <v-card-text class="px-3">
                                        <div class="tx-font-bold">
                                            * (-) Negative values indicate returned items
                                        </div>
                                        <div class="tx-font-bold">
                                            * Please note: A non-eligible transaction may be due to the purchase being classified as a B2B Sale or Non-Smart Select B2B Offer
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
            </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel value="server" id="server-section">
                    <v-expansion-panel-title aria-label="Server+ performance details, select to expand or collapse">
                        <v-icon class="me-2" aria-hidden="true">mdi-server</v-icon>
                        <LanguagePlaceholder default-text="Server+" />
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="pa-0">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
                <v-col cols="12" md="10" class="bg-color-white my-2 my-md-5 d-flex justify-center flex-column w-100 tx-font-bold px-2 px-md-0">  
                    <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                        <v-col cols="12" class="d-flex justify-start w-100 pa-0 py-5 py-md-10 ps-3 ps-md-10 tx-font-bold bg-color-quartz">  
                            <h2>
                                <LanguagePlaceholder default-text='Server+'></LanguagePlaceholder>
                            </h2>   
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="8">
                            <v-card color="#1D65C0" class="elevation-0">
                                <v-card-text>                                    
                                    <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units: " />
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.server_Units_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                    <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds: " />
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="12">
                            <v-timeline direction="horizontal">
                                <v-timeline-item
                                    :dot-color="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 5 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 5 && myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod < 9">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 1" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.2x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(5 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 9 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 9 && myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod < 13">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 2" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.3x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(9 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 13 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 13 && myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod < 17">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">

                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 3" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.4x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(13 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 17 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 17 && myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod < 21">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 4" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.5x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(17 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 21 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod >= 21">
                                            {{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 5" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.6x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(21 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                            </v-timeline>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Total Eligible Units:" /></div>
                                    <div><strong>{{ myPerformanceData.server_Units_ThisPeriod }} </strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds:" />
                                    </div>
                                    <div><strong>{{ myPerformanceData.server_UnitsCountTowardThreshold_ThisPeriod }}
                                        </strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 1 (5 Units | 1.2x Rewards)" /></div>
                                    <div><strong>5</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 2 (9 Units | 1.3x Rewards)" />
                                    </div>
                                    <div><strong>9</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 3 (13 Units | 1.4x Rewards)" />
                                    </div>
                                    <div><strong>13</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 4 (17 Units | 1.5x Rewards)" />
                                    </div>
                                    <div><strong>17</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 5 (21 Units | 1.6x Rewards)" />
                                    </div>
                                    <div><strong>21</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center">
                        <v-col cols="12">
                            <v-card tile flat>
                                <v-tabs v-model="serverActiveTab" color="primary" aria-label="Server+ data view tabs">
                                    <v-tab value="eligible" tabindex="0" aria-label="View Eligible Sales for Server+">
                                        <LanguagePlaceholder default-text="Eligible Sales" />
                                    </v-tab>
                                    <v-tab value="non-eligible" tabindex="0" aria-label="View Non Eligible Sales for Server+">
                                        <LanguagePlaceholder default-text="Non Eligible Sales" />
                                    </v-tab>
                                </v-tabs>
                                <v-toolbar density="compact" color="#EEEEEE">
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-download" @click="downloadFile_server" @keydown.enter="downloadFile_server" @keydown.space.prevent="downloadFile_server" aria-label="Download Server+ performance data" tabindex="0"><v-icon size="large">mdi-download</v-icon></v-btn>
                                </v-toolbar>
                                <v-tabs-window v-model="serverActiveTab">
                                    <v-tabs-window-item value="eligible">
                                        <v-card-text class="pa-0">
                                            <div class="overflow-x-auto">
                                                <v-text-field v-model="searchStringServer" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                    variant="outlined" hide-details single-line
                                                    aria-label="Filter Server+ eligible sales data"
                                                    :aria-describedby="'server-filter-help'"
                                                    id="server-filter">
                                                    <template v-slot:label>
                                                        <span>Filter Server+</span>
                                                    </template>
                                                </v-text-field>
                                                <div id="server-filter-help" class="sr-only">
                                                    <LanguagePlaceholder default-text="Type to filter the Server+ performance data table below" />
                                                </div>
                                                <v-data-table :items-per-page="10" :headers="headers" :search="searchStringServer"
                                                    :items="myPerformanceData.server_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                    id="server-performance-table"
                                                    role="table" aria-label="Server+ eligible sales performance data">
                                                    <template v-slot:item.rewards="{ item }">
                                                        {{ $filters.currencyUSD(item.rewards) }}
                                                    </template>
                                                    <template v-slot:footer.prepend>
                                                        <span id="server-items-per-page-label" class="sr-only">Items per page selector for Server+ table</span>
                                                    </template>
                                                    <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                        <span id="server-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in Server+ table`">
                                                            {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                        </span>
                                                    </template>
                                                </v-data-table>
                                            </div>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                    <v-tabs-window-item value="non-eligible">
                                        <v-card-text class="pa-0">
                                            <div class="overflow-x-auto">
                                                <v-text-field v-model="searchStringServerNonEligible" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                    variant="outlined" hide-details single-line
                                                    aria-label="Filter Server+ non eligible sales data"
                                                    :aria-describedby="'server-non-eligible-filter-help'"
                                                    id="server-non-eligible-filter">
                                                    <template v-slot:label>
                                                        <span>Filter Server+</span>
                                                    </template>
                                                </v-text-field>
                                                <div id="server-non-eligible-filter-help" class="sr-only">
                                                    <LanguagePlaceholder default-text="Type to filter the Server+ non eligible performance data table below" />
                                                </div>
                                                <v-data-table :items-per-page="10" :headers="nonEligibleHeaders" :search="searchStringServerNonEligible"
                                                    :items="myNonEligiblePerformanceData.server_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                    id="server-non-eligible-performance-table"
                                                    role="table" aria-label="Server+ non eligible sales performance data">
                                                    <template v-slot:item.rewards="{ item }">
                                                        {{ $filters.currencyUSD(item.rewards) }}
                                                    </template>
                                                    <template v-slot:footer.prepend>
                                                        <span id="server-non-eligible-items-per-page-label" class="sr-only">Items per page selector for Server+ non eligible table</span>
                                                    </template>
                                                    <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                        <span id="server-non-eligible-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in Server+ non eligible table`">
                                                            {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                        </span>
                                                    </template>
                                                </v-data-table>
                                            </div>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                </v-tabs-window>
                                <v-card>
                                    <v-card-text class="px-3">
                                        <div class="tx-font-bold">
                                            * (-) Negative values indicate returned items
                                        </div>
                                        <div class="tx-font-bold">
                                            * Please note: A non-eligible transaction may be due to the purchase being classified as a B2B Sale or Non-Smart Select B2B Offer
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
            </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel value="storage" id="storage-section">
                    <v-expansion-panel-title aria-label="Storage+ performance details, select to expand or collapse">
                        <v-icon class="me-2" aria-hidden="true">mdi-nas</v-icon>
                        <LanguagePlaceholder default-text="Storage+" />
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="pa-0">
            <v-row no-gutters class="bg-color-quartz d-flex justify-center w-100 pa-0">
                <v-col cols="12" md="1" class="bg-color-quartz"></v-col>
                <v-col cols="12" md="10" class="bg-color-white my-2 my-md-5 d-flex justify-center flex-column w-100 tx-font-bold px-2 px-md-0">  
                    <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                        <v-col cols="12" class="d-flex justify-start w-100 pa-0 py-5 py-md-10 ps-3 ps-md-10 tx-font-bold bg-color-quartz">  
                            <h2>
                                <LanguagePlaceholder default-text='Storage+'></LanguagePlaceholder>
                            </h2>   
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="8">
                            <v-card color="#1D65C0" class="elevation-0">
                                <v-card-text>
                                     <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Total Eligible Units:" /> 
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.storage_Units_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                    <v-row class="d-flex justify-end">
                                        <v-col cols="11" class="bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size16">
                                            <LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds:" />
                                        </v-col>
                                        <v-col cols="1" class="mt-n2 pe-5 bg-color-cobalt text-right tx-color-white tx-font-bolder tx-font-size24">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }}
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="end" class="d-none d-md-flex">
                        <v-col md="12">
                            <v-timeline direction="horizontal">
                                <v-timeline-item
                                    :dot-color="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 2 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 2 && myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod < 5">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 1" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.2x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(2 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 5 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 5 && myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod < 8">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }} Units
                                            Achieved
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 2" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.3x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(5 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 8 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 8 && myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod < 11">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">

                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 3" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.4x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(8 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 11 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 11 && myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod < 13">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 4" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.5x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(11 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                                <v-timeline-item
                                    :dot-color="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 13 ? 'light-green' : '#0C32A4'"
                                    size="small">
                                    <template v-slot:opposite>
                                        <v-chip variant="elevated" color="light-green-darken-3"
                                            v-if="myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod >= 13">
                                            {{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }} 
                                            <LanguagePlaceholder default-text="Units Achieved" />
                                        </v-chip>
                                    </template>
                                    <div class="text-body-2 text-center">
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="Threshold 5" />
                                        </div>
                                        <div class="text-subtitle-2">
                                            <LanguagePlaceholder default-text="(1.6x Rewards)" />
                                        </div>
                                        <div><LanguagePlaceholder default-text="(13 Units)" /></div>
                                    </div>
                                </v-timeline-item>
                            </v-timeline>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Total Eligible Units:" /></div>
                                    <div><strong>{{ myPerformanceData.storage_Units_ThisPeriod }} </strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card style="min-height: 6.25rem;" color="#0d2155">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units that count towards Reward Multiplier Thresholds:" />
                                    </div>
                                    <div><strong>{{ myPerformanceData.storage_UnitsCountTowardThreshold_ThisPeriod }}
                                        </strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 1 (2 Units | 1.2x Rewards)" /></div>
                                    <div><strong>2</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 2 (5 Units | 1.3x Rewards)" />
                                    </div>
                                    <div><strong>5</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 3 (8 Units | 1.4x Rewards)" />
                                    </div>
                                    <div><strong>8</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 4 (11 Units | 1.5x Rewards)" />
                                    </div>
                                    <div><strong>11</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center" class="d-sm-flex d-md-none">
                        <v-col cols="12">
                            <v-card color="#0672cb">
                                <v-card-text class="text-center">
                                    <div class="text--white"><LanguagePlaceholder default-text="Units to Achieve Threshold 5 (13 Units | 1.6x Rewards)" />
                                    </div>
                                    <div><strong>13</strong></div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row justify="center">
                        <v-col cols="12">
                            <v-card tile flat>
                                <v-tabs v-model="storageActiveTab" color="primary" aria-label="Storage+ data view tabs">
                                    <v-tab value="eligible" tabindex="0" aria-label="View Eligible Sales for Storage+">
                                        <LanguagePlaceholder default-text="Eligible Sales" />
                                    </v-tab>
                                    <v-tab value="non-eligible" tabindex="0" aria-label="View Non Eligible Sales for Storage+">
                                        <LanguagePlaceholder default-text="Non Eligible Sales" />
                                    </v-tab>
                                </v-tabs>
                                <v-toolbar density="compact" color="#EEEEEE">
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-download" @click="downloadFile_storage" @keydown.enter="downloadFile_storage" @keydown.space.prevent="downloadFile_storage" aria-label="Download Storage+ performance data" tabindex="0"><v-icon size="large">mdi-download</v-icon></v-btn>
                                </v-toolbar>
                                <v-tabs-window v-model="storageActiveTab">
                                    <v-tabs-window-item value="eligible">
                                        <v-card-text class="pa-0">
                                            <div class="overflow-x-auto">
                                                <v-text-field v-model="searchStringStorage" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                    variant="outlined" hide-details single-line
                                                    aria-label="Filter Storage+ eligible sales data"
                                                    :aria-describedby="'storage-filter-help'"
                                                    id="storage-filter">
                                                    <template v-slot:label>
                                                        <span>Filter Storage+</span>
                                                    </template>
                                                </v-text-field>
                                                <div id="storage-filter-help" class="sr-only">
                                                    <LanguagePlaceholder default-text="Type to filter the Storage+ performance data table below" />
                                                </div>
                                                <v-data-table :items-per-page="10" :headers="headers" :search="searchStringStorage"
                                                    :items="myPerformanceData.storage_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                    id="storage-performance-table"
                                                    role="table" aria-label="Storage+ eligible sales performance data">
                                                    <template v-slot:item.rewards="{ item }">
                                                        {{ $filters.currencyUSD(item.rewards) }}
                                                    </template>
                                                    <template v-slot:footer.prepend>
                                                        <span id="storage-items-per-page-label" class="sr-only">Items per page selector for Storage+ table</span>
                                                    </template>
                                                    <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                        <span id="storage-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in Storage+ table`">
                                                            {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                        </span>
                                                    </template>
                                                </v-data-table>
                                            </div>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                    <v-tabs-window-item value="non-eligible">
                                        <v-card-text class="pa-0">
                                            <div class="overflow-x-auto">
                                                <v-text-field v-model="searchStringStorageNonEligible" style="color:545454" prepend-inner-icon="mdi-filter-outline"
                                                    variant="outlined" hide-details single-line
                                                    aria-label="Filter Storage+ non eligible sales data"
                                                    :aria-describedby="'storage-non-eligible-filter-help'"
                                                    id="storage-non-eligible-filter">
                                                    <template v-slot:label>
                                                        <span>Filter Storage+</span>
                                                    </template>
                                                </v-text-field>
                                                <div id="storage-non-eligible-filter-help" class="sr-only">
                                                    <LanguagePlaceholder default-text="Type to filter the Storage+ non eligible performance data table below" />
                                                </div>
                                                <v-data-table :items-per-page="10" :headers="nonEligibleHeaders" :search="searchStringStorageNonEligible"
                                                    :items="myNonEligiblePerformanceData.storage_Transactions_ThisPeriod || []" class="v-data-table-row"
                                                    id="storage-non-eligible-performance-table"
                                                    role="table" aria-label="Storage+ non eligible sales performance data">
                                                    <template v-slot:item.rewards="{ item }">
                                                        {{ $filters.currencyUSD(item.rewards) }}
                                                    </template>
                                                    <template v-slot:footer.prepend>
                                                        <span id="storage-non-eligible-items-per-page-label" class="sr-only">Items per page selector for Storage+ non eligible table</span>
                                                    </template>
                                                    <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
                                                        <span id="storage-non-eligible-page-info" role="status" aria-live="polite" :aria-label="`Showing ${pageStart} to ${pageStop} of ${itemsLength} items in Storage+ non eligible table`">
                                                            {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
                                                        </span>
                                                    </template>
                                                </v-data-table>
                                            </div>
                                        </v-card-text>
                                    </v-tabs-window-item>
                                </v-tabs-window>
                                <v-card>
                                    <v-card-text class="px-3">
                                        <div class="tx-font-bold">
                                            * (-) Negative values indicate returned items
                                        </div>
                                        <div class="tx-font-bold">
                                            * Please note: A non-eligible transaction may be due to the purchase being classified as a B2B Sale or Non-Smart Select B2B Offer
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="1" class="bg-color-quartz"></v-col>
            </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>

            </v-expansion-panels>
        </section>
    
    </div>

        <!-- <v-dialog v-model="showDataDelayNotice" width="650" scrollable>
            <v-card>
                <v-toolbar color="#0d2155">
                    <v-toolbar-title><LanguagePlaceholder default-text="Important Notice" /></v-toolbar-title>
                </v-toolbar>
                <v-card-text class="pa-4">
                    <p class="my-2 general-text tx-font-bolder">
                        <LanguagePlaceholder default-text="Please note that there is a delay in the FY27Q2 Performance data update on RISE, we will inform you as soon as it is available." />
                    </p>
                </v-card-text>
                <v-card-actions class="border-t-thin">
                    <v-spacer></v-spacer>
                    <v-btn @click="showDataDelayNotice = false" width="100" color="#055699" variant="elevated"><LanguagePlaceholder default-text="Close" /></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog> -->

</template>


<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useSystemStore } from '@/stores/system'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import imgUrl_my_performance from '@/assets/img/banner/blue_myperformance.jpg'
import PartnerRewardsProgress from '@/components/PartnerRewardsProgress.vue'
import { useExcelStore } from '@/stores/excel'

const systemStore = useSystemStore()
const excelStore = useExcelStore()

const incentivePeriodLabelId = 'my-performance-incentive-period-label'

const showDataDelayNotice = ref(true)

const quarterSelectRef = ref(null)
const quarterMenuOpen = ref(false)

const quarters = ref([])
const selectedQuarter = ref('')
const myPerformanceData = ref({})
const myNonEligiblePerformanceData = ref({})
const rewardLoading = ref(false)

const searchStringPCDP = ref('')
const searchStringServer = ref('')
const searchStringStorage = ref('')

const searchStringPCDPNonEligible = ref('')
const searchStringServerNonEligible = ref('')
const searchStringStorageNonEligible = ref('')

const searchStringNonEligibleNew = ref('')

const openPanels = ref(['pcdp', 'server', 'storage'])

const allNonEligibleTransactions = computed(() => [
    ...(myNonEligiblePerformanceData.value.csG_Transactions_ThisPeriod || []),
    ...(myNonEligiblePerformanceData.value.server_Transactions_ThisPeriod || []),
    ...(myNonEligiblePerformanceData.value.storage_Transactions_ThisPeriod || []),
])

const pcdpActiveTab = ref('eligible')
const serverActiveTab = ref('eligible')
const storageActiveTab = ref('eligible')

// Associate items-per-page selectors and pagination controls with their tables
const associatePaginationControls = () => {
    nextTick(() => {
        // PCs+ Displays+Peripherals table
        const pcdpTable = document.getElementById('pcdp-performance-table')
        if (pcdpTable) {
            const tableLabel = 'PCs+ Displays+Peripherals'
            const pcdpSelector = pcdpTable.querySelector('.v-data-table-footer select, .v-data-table-footer__items-per-page select, select[aria-label*="items per page"], .v-select select')
            if (pcdpSelector) {
                pcdpSelector.setAttribute('aria-labelledby', 'pcdp-items-per-page-label')
                pcdpSelector.setAttribute('aria-controls', 'pcdp-performance-table')
                pcdpSelector.setAttribute('aria-label', `Items per page for ${tableLabel} table`)
            }
            
            // Label pagination buttons
            const firstBtn = pcdpTable.querySelector('.v-data-table-footer__first-page-button, button[aria-label*="first"]')
            if (firstBtn && !firstBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                firstBtn.setAttribute('aria-label', `First page of ${tableLabel} table`)
            }
            
            const prevBtn = pcdpTable.querySelector('.v-data-table-footer__prev-page-button, button[aria-label*="previous"]')
            if (prevBtn && !prevBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                prevBtn.setAttribute('aria-label', `Previous page of ${tableLabel} table`)
            }
            
            const nextBtn = pcdpTable.querySelector('.v-data-table-footer__next-page-button, button[aria-label*="next"]')
            if (nextBtn && !nextBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                nextBtn.setAttribute('aria-label', `Next page of ${tableLabel} table`)
            }
            
            const lastBtn = pcdpTable.querySelector('.v-data-table-footer__last-page-button, button[aria-label*="last"]')
            if (lastBtn && !lastBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                lastBtn.setAttribute('aria-label', `Last page of ${tableLabel} table`)
            }
            
            // Label pagination navigation region - target the inner nav to remove redundant "navigation" from label
            const paginationNav = pcdpTable.querySelector('.v-data-table-footer nav') || pcdpTable.querySelector('nav[role="navigation"]')
            if (paginationNav) {
                paginationNav.setAttribute('aria-label', `Pagination - ${tableLabel} table`)
            }
        }
        
        // Server+ table
        const serverTable = document.getElementById('server-performance-table')
        if (serverTable) {
            const tableLabel = 'Server+'
            const serverSelector = serverTable.querySelector('.v-data-table-footer select, .v-data-table-footer__items-per-page select, select[aria-label*="items per page"], .v-select select')
            if (serverSelector) {
                serverSelector.setAttribute('aria-labelledby', 'server-items-per-page-label')
                serverSelector.setAttribute('aria-controls', 'server-performance-table')
                serverSelector.setAttribute('aria-label', `Items per page for ${tableLabel} table`)
            }
            
            const firstBtn = serverTable.querySelector('.v-data-table-footer__first-page-button, button[aria-label*="first"]')
            if (firstBtn && !firstBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                firstBtn.setAttribute('aria-label', `First page of ${tableLabel} table`)
            }
            
            const prevBtn = serverTable.querySelector('.v-data-table-footer__prev-page-button, button[aria-label*="previous"]')
            if (prevBtn && !prevBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                prevBtn.setAttribute('aria-label', `Previous page of ${tableLabel} table`)
            }
            
            const nextBtn = serverTable.querySelector('.v-data-table-footer__next-page-button, button[aria-label*="next"]')
            if (nextBtn && !nextBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                nextBtn.setAttribute('aria-label', `Next page of ${tableLabel} table`)
            }
            
            const lastBtn = serverTable.querySelector('.v-data-table-footer__last-page-button, button[aria-label*="last"]')
            if (lastBtn && !lastBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                lastBtn.setAttribute('aria-label', `Last page of ${tableLabel} table`)
            }
            
            const paginationNav = serverTable.querySelector('.v-data-table-footer nav') || serverTable.querySelector('nav[role="navigation"]')
            if (paginationNav) {
                paginationNav.setAttribute('aria-label', `Pagination - ${tableLabel} table`)
            }
        }
        
        // Storage+ table
        const storageTable = document.getElementById('storage-performance-table')
        if (storageTable) {
            const tableLabel = 'Storage+'
            const storageSelector = storageTable.querySelector('.v-data-table-footer select, .v-data-table-footer__items-per-page select, select[aria-label*="items per page"], .v-select select')
            if (storageSelector) {
                storageSelector.setAttribute('aria-labelledby', 'storage-items-per-page-label')
                storageSelector.setAttribute('aria-controls', 'storage-performance-table')
                storageSelector.setAttribute('aria-label', `Items per page for ${tableLabel} table`)
            }
            
            const firstBtn = storageTable.querySelector('.v-data-table-footer__first-page-button, button[aria-label*="first"]')
            if (firstBtn && !firstBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                firstBtn.setAttribute('aria-label', `First page of ${tableLabel} table`)
            }
            
            const prevBtn = storageTable.querySelector('.v-data-table-footer__prev-page-button, button[aria-label*="previous"]')
            if (prevBtn && !prevBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                prevBtn.setAttribute('aria-label', `Previous page of ${tableLabel} table`)
            }
            
            const nextBtn = storageTable.querySelector('.v-data-table-footer__next-page-button, button[aria-label*="next"]')
            if (nextBtn && !nextBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                nextBtn.setAttribute('aria-label', `Next page of ${tableLabel} table`)
            }
            
            const lastBtn = storageTable.querySelector('.v-data-table-footer__last-page-button, button[aria-label*="last"]')
            if (lastBtn && !lastBtn.getAttribute('aria-label')?.includes(tableLabel)) {
                lastBtn.setAttribute('aria-label', `Last page of ${tableLabel} table`)
            }
            
            const paginationNav = storageTable.querySelector('.v-data-table-footer nav') || storageTable.querySelector('nav[role="navigation"]')
            if (paginationNav) {
                paginationNav.setAttribute('aria-label', `Pagination - ${tableLabel} table`)
            }
        }
    })
}

onMounted(() => {
    // Set role="combobox" on the inner <input> so screen readers enter forms mode
    // and arrow keys are passed through to Vuetify's dropdown navigation.
    // (role on the outer div wrapper has no effect on SR forms-mode switching.)
    nextTick(() => {
        const input = quarterSelectRef.value?.$el?.querySelector('input')
        if (input) {
            input.setAttribute('role', 'combobox')
            input.removeAttribute('title')
        }
    })

    systemStore.getQuarters().then(result => {
    if (result.success) {
        quarters.value = result.quarters
                .filter(q => q.currentQuarters === true)
                .map(q => q.quarter)
      
      selectedQuarter.value = result.quarters.find(q => q.currentQuarter === true)?.quarter

      getData()
    }
    }).catch(error => {
      console.log('recalculateLocalValue', error)
    })

    // Associate pagination controls after component mounts and data loads
    setTimeout(() => {
        associatePaginationControls()
    }, 500)
    
    // Set up observer to watch for pagination control changes (with debounce)
    let observerTimeout
    const observer = new MutationObserver(() => {
        clearTimeout(observerTimeout)
        observerTimeout = setTimeout(() => {
            associatePaginationControls()
        }, 300)
    })
    
    // Observe each table for DOM changes (when pagination updates)
    setTimeout(() => {
        nextTick(() => {
            const pcdpTable = document.getElementById('pcdp-performance-table')
            const serverTable = document.getElementById('server-performance-table')
            const storageTable = document.getElementById('storage-performance-table')
            
            if (pcdpTable) observer.observe(pcdpTable, { childList: true, subtree: true, attributes: false })
            if (serverTable) observer.observe(serverTable, { childList: true, subtree: true, attributes: false })
            if (storageTable) observer.observe(storageTable, { childList: true, subtree: true, attributes: false })
        })
    }, 500)
})

// Watch for data changes and reassociate pagination controls
watch(() => myPerformanceData.value, () => {
    nextTick(() => {
        setTimeout(() => {
            associatePaginationControls()
        }, 300)
    })
}, { deep: true })

const getData = () => {
    rewardLoading.value = true
    systemStore.getMyPerformanceData(systemStore.userDetail.affinityId, selectedQuarter.value).then(result => {
        myPerformanceData.value = result
        rewardLoading.value = false
    })
    systemStore.getMyNonEligiblePerformanceData(systemStore.userDetail.affinityId, selectedQuarter.value).then(result => {
        myNonEligiblePerformanceData.value = result
    })
}

const clickSubmit = () => {
    getData()
}

const downloadFile_csg = () => {
    const data = pcdpActiveTab.value === 'eligible'
        ? myPerformanceData.value.csG_Transactions_ThisPeriod
        : myNonEligiblePerformanceData.value.csG_Transactions_ThisPeriod
    if (!data || data.length === 0) return
    excelStore.generateInMemoryExcelDocument(data, 'MyPerformanceDocument').catch(error => {
        console.log('Generate In Memory Excel Document - CSG', error)
    })
}

const downloadFile_server = () => {
    const data = serverActiveTab.value === 'eligible'
        ? myPerformanceData.value.server_Transactions_ThisPeriod
        : myNonEligiblePerformanceData.value.server_Transactions_ThisPeriod
    if (!data || data.length === 0) return
    excelStore.generateInMemoryExcelDocument(data, 'MyPerformanceDocument').catch(error => {
        console.log('Generate In Memory Excel Document - Server', error)
    })
}

const downloadFile_storage = () => {
    const data = storageActiveTab.value === 'eligible'
        ? myPerformanceData.value.storage_Transactions_ThisPeriod
        : myNonEligiblePerformanceData.value.storage_Transactions_ThisPeriod
    if (!data || data.length === 0) return
    excelStore.generateInMemoryExcelDocument(data, 'MyPerformanceDocument').catch(error => {
        console.log('Generate In Memory Excel Document - Storage', error)
    })
}

const downloadFileNew_all = () => {
    const data = allNonEligibleTransactions.value
    if (!data || data.length === 0) return
    excelStore.generateInMemoryExcelDocument(data, 'MyPerformanceDocument').catch(error => {
        console.log('Generate In Memory Excel Document - All Non-Eligible', error)
    })
}

const onDataTableHeaderKeydown = (e) => {
    // Make Vuetify header cells keyboard actionable + navigable (Enter/Space + arrows).
    if (!e) return
    const key = e.key
    const current = e.currentTarget

    const moveFocus = (direction) => {
        const row = current?.closest?.('tr')
        if (!row) return

        const headers = Array.from(row.querySelectorAll('th[tabindex="0"], th[tabindex="0"] *')).map(el => (el.tagName === 'TH' ? el : el.closest('th'))).filter(Boolean)
        // Deduplicate in case query picks descendants
        const unique = [...new Set(headers)]
        const index = unique.indexOf(current)
        if (index < 0) return

        let nextIndex = index
        if (direction === 'prev') nextIndex = Math.max(0, index - 1)
        if (direction === 'next') nextIndex = Math.min(unique.length - 1, index + 1)
        if (direction === 'first') nextIndex = 0
        if (direction === 'last') nextIndex = unique.length - 1

        const next = unique[nextIndex]
        if (next && next !== current) next.focus?.()
    }

    if (key === 'Enter' || key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        current?.click?.()
        return
    }

    if (key === 'ArrowLeft') {
        e.preventDefault()
        e.stopPropagation()
        moveFocus('prev')
        return
    }

    if (key === 'ArrowRight') {
        e.preventDefault()
        e.stopPropagation()
        moveFocus('next')
        return
    }

    if (key === 'Home') {
        e.preventDefault()
        e.stopPropagation()
        moveFocus('first')
        return
    }

    if (key === 'End') {
        e.preventDefault()
        e.stopPropagation()
        moveFocus('last')
        return
    }
}


// cellProps
const headers = [
    { title: 'Product Group', value: 'productGroup', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: '* Quantity', value: 'quantity', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: '* $ Rewards', value: 'rewards', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Counts Towards Threshold', value: 'countTowardsThreshold', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Partner', value: 'eligiblePartner', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Product', value: 'eligibleProduct', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Distributor', value: 'eligibleDistributor', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
]

const nonEligibleHeaders = [
    { title: 'Product Group', value: 'productGroup', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Quantity', value: 'quantity', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Rewards (USD)', value: 'rewards', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Count Towards Threshold', value: 'countTowardsThreshold', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Partner', value: 'eligiblePartner', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Product', value: 'eligibleProduct', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Distributor', value: 'eligibleDistributor', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
    { title: 'Eligible Transaction', value: 'eligibleTransaction', sortable: true, headerProps: { class: 'v-data-table-header', tabindex: 0, role: 'columnheader', onKeydown: onDataTableHeaderKeydown } },
]

const items = [

]

</script>

<style scoped>
/* High zoom accessibility support - WCAG 2.0 SC 1.4.4 and 1.4.10 */
@media (min-resolution: 192dpi), (min-resolution: 2dppx) {
  /* Support for high DPI displays and zoom */
  .bg-color-granite {
    max-width: 100%;
    overflow-x: auto;
  }
}

/* Ensure cards wrap properly at high zoom */
.d-flex.flex-row {
  flex-wrap: wrap;
}

/* Prevent horizontal overflow at high zoom */
.bg-color-granite {
  max-width: 100%;
  box-sizing: border-box;
}

/* Ensure text remains readable at high zoom */
.tx-font-size32 {
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Responsive adjustments for very high zoom (200%+) */
@media screen and (min-width: 1px) {
  /* Ensure all containers respect viewport width */
  .w-auto {
    max-width: 100%;
  }
  
  /* Allow content to expand vertically */
  .h-100 {
    min-height: auto;
    height: auto;
  }
}

/* Ensure proper spacing at high zoom without causing overflow */
@media (min-width: 1px) {
  .mx-md-10 {
    margin-left: clamp(0.5rem, 2.5vw, 2.5rem);
    margin-right: clamp(0.5rem, 2.5vw, 2.5rem);
  }
}

/* Ensure data tables are scrollable at high zoom */
.overflow-x-auto {
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}

/* Ensure tables don't break layout at high zoom */
.v-data-table-row {
  min-width: 0;
  width: 100%;
}

/* Prevent text overflow in table cells */
.v-data-table-row td {
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* Ensure header image scales properly at high zoom */
.v-img {
  max-width: 100%;
  height: auto;
}

/* Ensure page header text remains readable at high zoom */
.page-header {
  max-width: 90%;
  left: 50%;
  transform: translateX(-50%);
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Accordion panel title focus indicator */
:deep(.v-expansion-panel-title:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 2px #FFFFFF !important;
  position: relative !important;
  z-index: 10 !important;
}

/*
  WCAG 2.0 Focus Indicators — dual-ring pattern:
    outer ring : #003785 dark navy (~13:1 vs white)
    inner halo : #FFFFFF white   (~8:1 vs dark toolbar #444444)
  Visible on both light and dark backgrounds.
*/
:deep(.v-btn:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 4px !important;
  box-shadow: 0 0 0 4px #FFFFFF !important;
  position: relative !important;
  z-index: 10 !important;
}

/*
  v-select / v-text-field: outer wrapper div is not natively focusable.
  Use Vuetify's own .v-field--focused class AND :focus-within as fallback.
*/
:deep(.v-select .v-field--focused),
:deep(.v-select .v-field:focus-within) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 2px #FFFFFF !important;
}

:deep(.v-text-field .v-field--focused),
:deep(.v-text-field .v-field:focus-within) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 2px #FFFFFF !important;
}

:deep(.v-timeline-item:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 4px !important;
  box-shadow: 0 0 0 4px #FFFFFF !important;
}

:deep(.v-chip:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 3px !important;
  box-shadow: 0 0 0 3px #FFFFFF !important;
}

:deep(.v-data-table-footer button:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 4px !important;
  box-shadow: 0 0 0 4px #FFFFFF !important;
  position: relative !important;
  z-index: 10 !important;
}

:deep(.v-data-table-footer .v-select .v-field--focused),
:deep(.v-data-table-footer .v-select .v-field:focus-within) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 2px #FFFFFF !important;
}

/* Table sort headers — th[tabindex="0"] is natively focusable so :focus-visible fires */
:deep(.v-data-table thead th[tabindex="0"]:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 2px #FFFFFF !important;
  background-color: rgba(0, 55, 133, 0.08) !important;
  position: relative !important;
  z-index: 10 !important;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  :deep(.v-btn:focus-visible),
  :deep(.v-data-table-footer button:focus-visible),
  :deep(.v-data-table thead th[tabindex="0"]:focus-visible) {
    outline-width: 4px !important;
    outline-color: #000000 !important;
    box-shadow: 0 0 0 4px #FFFFFF !important;
  }
}

/* WCAG 1.4.4 / 1.4.10 — Reflow at 320px / Resize Text at 200%+ zoom.
   Prevent content overlap and loss at high zoom levels. */
@media (max-width: 960px) {
  :deep(.mx-10) {
    margin-left: 0.5rem !important;
    margin-right: 0.5rem !important;
  }
  :deep(.w-25) {
    width: 100% !important;
    max-width: 100% !important;
  }
  :deep(.ps-14) {
    padding-left: 0 !important;
  }
  :deep(.flex-column.flex-md-row) {
    flex-direction: column !important;
  }
}

@media (max-width: 599px) {
  :deep(.v-data-table) {
    overflow-x: auto;
    display: block;
    width: 100%;
  }
  :deep(.v-card) {
    min-width: 0;
  }
}

</style>